/**
 * AI Q&A chat hooks for research assistant
 *
 * Provides:
 * - useAskQuestion: mutation for sending questions to AI
 * - useSuggestions: query for dynamic suggested questions
 * - useConversation: state management for chat history
 */
import { useState, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { askQuestion, getSuggestions } from '../api/research'
import type {
  AskRequest,
  AskResponse,
  SuggestionsResponse,
  ChatMessage,
  ConversationMessage,
} from '../types/ai-qa'

// =============================================================================
// Query Keys
// =============================================================================

export const AI_CHAT_KEYS = {
  suggestions: (symbol: string) => ['ai-suggestions', symbol] as const,
}

// =============================================================================
// Suggestions Hook
// =============================================================================

/**
 * Fetch dynamic suggested questions for a symbol
 * @param symbol Stock ticker symbol (undefined disables query)
 */
export function useSuggestions(symbol: string | undefined) {
  return useQuery<SuggestionsResponse>({
    queryKey: AI_CHAT_KEYS.suggestions(symbol ?? ''),
    queryFn: () => getSuggestions(symbol!),
    enabled: !!symbol,
    staleTime: 5 * 60 * 1000, // 5 minutes - suggestions don't change frequently
  })
}

// =============================================================================
// Ask Question Mutation
// =============================================================================

interface UseAskQuestionOptions {
  onSuccess?: (response: AskResponse) => void
  onError?: (error: Error) => void
}

/**
 * Mutation for asking questions to AI
 */
export function useAskQuestion(options?: UseAskQuestionOptions) {
  const queryClient = useQueryClient()

  return useMutation<AskResponse, Error, AskRequest>({
    mutationFn: askQuestion,
    onSuccess: (data) => {
      // Invalidate suggestions after asking a question (they might change)
      queryClient.invalidateQueries({ queryKey: ['ai-suggestions'] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
  })
}

// =============================================================================
// Conversation State Hook
// =============================================================================

/**
 * Generate unique ID for messages
 */
function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Generate unique conversation ID
 */
function generateConversationId(): string {
  return `conv_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Convert chat history to API format (strips UI-specific fields)
 */
function messagesToChatHistory(messages: ConversationMessage[]): ChatMessage[] {
  return messages.map(({ role, content }) => ({ role, content }))
}

interface UseConversationOptions {
  /** Initial symbols for context (e.g., from Research page) */
  initialSymbols?: string[]
  /** Portfolio ID for filtering philosophy allocations */
  portfolioId?: string | null
}

/**
 * Hook for managing conversation state with AI chat
 *
 * @param options Configuration options including initialSymbols and portfolioId
 */
export function useConversation(options: UseConversationOptions = {}) {
  const { initialSymbols = [], portfolioId = null } = options
  const [conversationId, setConversationId] = useState<string>(generateConversationId())
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [symbols, setSymbols] = useState<string[]>(initialSymbols)
  const [error, setError] = useState<string | undefined>()

  const askMutation = useAskQuestion({
    onSuccess: (response) => {
      // Add assistant response to conversation
      const assistantMessage: ConversationMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: response.answer,
        timestamp: new Date(),
        sources: response.sources,
        tokensUsed: response.tokens_used,
      }
      setMessages((prev) => [...prev, assistantMessage])
      setConversationId(response.conversation_id)
      setError(undefined)
    },
    onError: (err) => {
      setError(err.message || 'Failed to get response from AI')
    },
  })

  /**
   * Send a question to the AI
   */
  const sendQuestion = useCallback(
    (question: string, additionalSymbols?: string[]) => {
      // Build effective symbols list
      const effectiveSymbols =
        additionalSymbols && additionalSymbols.length > 0
          ? additionalSymbols
          : symbols

      if (effectiveSymbols.length === 0) {
        setError('Please select at least one symbol')
        return
      }

      // Add user message to conversation immediately
      const userMessage: ConversationMessage = {
        id: generateMessageId(),
        role: 'user',
        content: question,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, userMessage])

      // Build request with history (max 10 messages)
      const history = messagesToChatHistory(messages).slice(-10)

      const request: AskRequest = {
        question,
        symbols: effectiveSymbols.slice(0, 5), // Max 5 symbols
        conversation_id: conversationId,
        history,
        portfolio_id: portfolioId,
      }

      askMutation.mutate(request)
    },
    [symbols, messages, conversationId, portfolioId, askMutation]
  )

  /**
   * Clear conversation and start fresh
   */
  const clearConversation = useCallback(() => {
    setConversationId(generateConversationId())
    setMessages([])
    setError(undefined)
  }, [])

  /**
   * Update symbols for context
   */
  const updateSymbols = useCallback((newSymbols: string[]) => {
    setSymbols(newSymbols)
  }, [])

  return {
    // State
    conversationId,
    messages,
    symbols,
    isLoading: askMutation.isPending,
    error,

    // Actions
    sendQuestion,
    clearConversation,
    updateSymbols,
  }
}

// =============================================================================
// Re-exports
// =============================================================================

export type { AskRequest, AskResponse, SuggestionsResponse }
